import {Component, HostListener, OnInit} from '@angular/core';
import {Asset} from '../../model/asset';
import {AssetFilters} from '../../model/filters/asset-filters';
import {AssetsService} from '../../services/assets.service';
import {SortOrder} from '../../model/sort-order';
import {ConfirmationService, MenuItem, MessageService} from 'primeng/api';
import {TranslocoService} from '@ngneat/transloco';
import {CommonMessagesService} from '../../services/common-messages.service';
import {TenantsService} from '../../services/tenants.service';
import {AuthGuard} from '../../common/auth-guard.service';
import {Tenant} from '../../model/tenant';
import {AssetRepresentationsService} from '../../services/asset-representations.service';
import {Organization} from '../../model/organization';
import {OrganizationsService} from '../../services/organizations.service';
import {FiltersService} from "../../services/filters.service";
import {SharedResource} from "../../model/shared-resource";

@Component({
  selector: 'app-assets',
  templateUrl: './assets.component.html',
  styleUrls: ['./assets.component.scss']
})
export class AssetsComponent implements OnInit {

  public assets: Asset[];
  public totalAssets: number;
  public loadingAssets: boolean;
  public tenants: Tenant[];
  public organizations: Organization[];
  public assetRepresentations: MenuItem[];

  public rows = 10;
  public first = 0;
  public selected = [];

  public filtersVisible: boolean;
  public currentFilters: AssetFilters;
  public filtersFilled: boolean;

  public sharedResources: Array<SharedResource>;
  public showDialog: boolean = false;
  public exportVisible: boolean;

  constructor(private assetsService: AssetsService,
              private messageService: MessageService,
              private assetRepresentationsService: AssetRepresentationsService,
              private organizationsService: OrganizationsService,
              private i18n: TranslocoService,
              private tenantsService: TenantsService,
              public authGuardService: AuthGuard,
              private confirmationService: ConfirmationService,
              private commonMessagesService: CommonMessagesService,
              private filtersService: FiltersService<AssetFilters>) {
    this.currentFilters = new AssetFilters();
  }

  ngOnInit(): void {
    if (this.filtersService.getCurrentFilters(this.constructor.name) !== undefined) {
      this.filtersFilled = true;
    }
    this.loadFilters().then(() => {
      Object.assign(this.currentFilters, this.filtersService.getCurrentFilters(this.constructor.name));
    });
    this.assets = new Array(this.rows);
  }

  private loadAssets(offset?: number, limit?: number): void {
    this.loadingAssets = true;
    this.assets = new Array(this.rows);
    this.trimFieldsForm(this.currentFilters);
    this.assetsService.getAssets(offset, limit, this.filtersService.getCurrentFilters(this.constructor.name) === undefined ? this.currentFilters : this.filtersService.getCurrentFilters(this.constructor.name))
      .subscribe(response => {
        this.assets = response.assets;
        this.totalAssets = response.totalElements;
        this.loadingAssets = false;
      });
  }

  onPageChange(event): void {
    this.currentFilters.sortField = event.sortField;
    this.currentFilters.sortOrder = event.sortOrder < 0 ? SortOrder.DESCENDING : SortOrder.ASCENDING;
    this.loadAssets(event.first / event.rows, event.rows);
  }

  resetFilters(): void {
    this.currentFilters = new AssetFilters();
    this.currentFilters.sortField = 'alias';
    this.currentFilters.sortOrder = SortOrder.ASCENDING;
    this.filtersFilled = false;
    this.filtersService.setCurrentFilters(undefined, this.constructor.name);
    this.applyFilters(true);
  }

  applyFilters(reset?: boolean): void {
    this.first = 0;
    if (reset !== true) {
      this.filtersService.setCurrentFilters(this.currentFilters, this.constructor.name);
      this.filtersFilled = true;
    }
    this.loadAssets(this.first / this.rows, this.rows);
  }

  confirmAndDelete(asset: Asset): void {
    this.commonMessagesService.showDeleteRequestConfirmation(() => {
      this.deleteAsset(asset.uuid);
    });
  }

  deleteAsset(uuid: string): void {
    this.assetsService.deleteAsset(uuid).toPromise().then(() => {
      this.commonMessagesService.showSuccessfullyDeletedMessage();
      this.loadAssets(this.first / this.rows, this.rows);
    }, error => {
      this.commonMessagesService.showErrorWhileDeletingMessage(error.status);
    });
  }

  confirmAndDeleteSelected(): void {
    this.commonMessagesService.showDeleteRequestConfirmation(() => this.deleteSelected(), true);
  }

  deleteSelected(): void {
    if (this.selected.length === 0) {
      return;
    }
    const uuids = this.selected.map(item => item.uuid);

    this.assetsService.deleteAssets(uuids).toPromise().then(() => {
      this.commonMessagesService.showSuccessfullyDeletedMessage();
      this.loadAssets(this.first / this.rows, this.rows);
    }, error => {
      this.commonMessagesService.showErrorWhileDeletingMessage(error.status, true);
    });
  }

  showSharedDialog(sharedResource: Asset): void {
    this.sharedResources = [new SharedResource('ASSET', sharedResource.alias, sharedResource.uuid,
      sharedResource.tenant.uuid, sharedResource.organization.uuid)];
    this.showDialog = true;
  }

  trimFieldsForm(filters: AssetFilters): void {
    Object.keys(filters).forEach((key) => {
      if (filters[key] !== undefined && typeof filters[key] === 'string') {
        filters[key] = filters[key].trim();
      }
    });
  }

  get filteredOrganizations(): Organization[] {
    if (this.authGuardService.superAdminRequired()) {
      if (this.currentFilters.tenantUuids?.length > 0) {
        return this.organizations.filter(org => this.currentFilters.tenantUuids.includes(org.tenant.uuid));
      }
    }
    return this.organizations;
  }

  private loadFilters(): Promise<any> {
    let promises: Array<Promise<any>> = [this.assetRepresentationsService.getAssetRepresentations().toPromise(), this.organizationsService.getOrganizations().toPromise()];
    if (this.authGuardService.superAdminRequired()) {
      promises.push(this.tenantsService.getTenants().toPromise());
    }
    return Promise.all(promises).then(response => {
      this.assetRepresentations = response[0].assetRepresentations
        .map(category => category.name)
        .filter((v, i, a) => a.indexOf(v) === i)
        .sort()
        .map(categoryName => {
          return {
            label: categoryName,
            value: categoryName
          };
        });
      this.organizations = response[1].organizations.sort((a, b) => a.name > b.name ? 1 : -1);
      if (this.authGuardService.superAdminRequired()) {
        this.tenants = response[2].tenants.sort((a, b) => a.name > b.name ? 1 : -1);
      }
    });
  }

  @HostListener('window:keyup', ['$event'])
  keyEvent(event: KeyboardEvent): void {
    if (event.key === 'f' && !this.showDialog) {
      this.filtersVisible = true;
    }
    if (event.key === 'Escape') {
      this.filtersVisible = false;
    }
  }

}
